import type { RouteObject } from 'react-router-dom';

// These routes form the local-first workspace shell. Start loading their
// chunks together so an online workspace can keep navigating after it goes
// offline, while less common routes remain on demand.
const localFirstRouteModules = {
  all: import('./pages/workspace/all-page/all-page'),
  detail: import('./pages/workspace/detail-page/detail-page'),
  trash: import('./pages/workspace/trash-page'),
};

/**
 * Retry a dynamic import with exponential backoff.
 * Catches chunk loading failures (e.g. during dev server restart)
 * and returns a graceful fallback component instead of crashing.
 */
function lazyWithRetry(
  importFn: () => Promise<any>,
  fallbackMessage: string,
  retries = 3,
  delay = 1000
): () => Promise<any> {
  return async () => {
    for (let i = 0; i < retries; i++) {
      try {
        return await importFn();
      } catch {
        if (i < retries - 1) {
          await new Promise(r => setTimeout(r, delay * (i + 1)));
        }
      }
    }
    // All retries exhausted — return fallback component
    const { LazyLoadFallback } =
      await import('./pages/workspace/lazy-fallback');
    return {
      Component: () => LazyLoadFallback({ message: fallbackMessage }),
    };
  };
}

export const workbenchRoutes = [
  {
    path: '/chat',
    lazy: () => import('./pages/workspace/chat/index'),
  },
  {
    path: '/all',
    lazy: () => localFirstRouteModules.all,
  },
  {
    path: '/collection',
    lazy: () => import('./pages/workspace/all-collection'),
  },
  {
    path: '/collection/:collectionId',
    lazy: () => import('./pages/workspace/collection/index'),
  },
  {
    path: '/tag',
    lazy: () => import('./pages/workspace/all-tag'),
  },
  {
    path: '/tag/:tagId',
    lazy: () => import('./pages/workspace/tag'),
  },
  {
    path: '/trash',
    lazy: () => localFirstRouteModules.trash,
  },
  {
    path: '/planner',
    lazy: () => import('./pages/workspace/calendar'),
  },
  {
    // sl-i7kr.14.12.1: Open a root doc's Custom App inside the page shell.
    path: '/:pageId/custom-app/:viewId',
    lazy: lazyWithRetry(
      () => import('./pages/workspace/custom-apps/browser-shell'),
      'Loading Custom App...'
    ),
  },
  {
    path: '/:pageId',
    lazy: () => localFirstRouteModules.detail,
  },
  {
    path: '/:pageId/attachments/:attachmentId',
    lazy: () => import('./pages/workspace/attachment/index'),
  },
  {
    path: '/journals',
    lazy: () => import('./pages/workspace/journals'),
  },
  {
    path: '/settings',
    lazy: () => import('./pages/workspace/settings'),
  },
  {
    // sl-sdk6.6.3: Graph Explorer — Enterprise-only knowledge graph visualization
    path: '/graph-explorer',
    lazy: lazyWithRetry(
      () => import('./pages/workspace/graph-explorer/index'),
      'Connecting to Knowledge Graph...'
    ),
  },
  {
    // sl-u319.2.1: Marketplace page — Browse, Installed, My Published tabs
    path: '/marketplace',
    lazy: () => import('./pages/workspace/marketplace'),
  },
  {
    // Workflows page — standalone Workflow Builder, no Editor dependency
    path: '/workflows',
    lazy: () => import('./pages/workspace/workflows'),
  },
  {
    // Workspace Mail — macOS-Mail-like main workspace viewport
    path: '/mail',
    lazy: lazyWithRetry(
      () => import('./pages/workspace/mail'),
      'Loading Mail...'
    ),
  },
  {
    // sl-bpu6.1.1: Custom Apps list page
    path: '/custom-apps',
    lazy: lazyWithRetry(
      () => import('./pages/workspace/custom-apps/index'),
      'Loading Custom Apps...'
    ),
  },
  {
    // sl-bpu6.1.1: Custom Apps editor page
    path: '/custom-apps/:viewId',
    lazy: lazyWithRetry(
      () => import('./pages/workspace/custom-apps/editor'),
      'Loading Editor...'
    ),
  },
  {
    path: '*',
    lazy: () => import('./pages/404'),
  },
] satisfies RouteObject[];
